import React, { useLayoutEffect, useRef } from 'react';
import { Helmet } from '@dr.pogodin/react-helmet';
import { gsap } from 'gsap';

import mack from '../assets/mack.png';
import workshop from '../assets/workshop.png';
import three from '../assets/three.png';
import tartan from '../assets/tartan.png';

const OurStory = () => {
  const heroRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(".story-hero h1, .story-hero p", {
        opacity: 0,
        y: 50,
        duration: 1,
        stagger: 0.2,
      });
    }, heroRef);
    return () => ctx.revert();
  }, []);

  return (
    <>
      <Helmet>
        <title>Our Story | Four Generations of Kiltmaking | Mackintosh's Kilts</title>
        <meta name="description" content="Discover the Mackintosh family legacy - four generations of master kiltmakers preserving Scottish tradition. From Alistair's founding vision to today's modern craftsmanship." />
        <meta name="keywords" content="Mackintosh family, kiltmaking history, Scottish heritage, traditional kiltmakers, family business, Glasgow kiltmakers, generational craftsmanship" />
        <meta property="og:title" content="Our Story | Four Generations of Kiltmaking" />
        <meta property="og:description" content="The Mackintosh name is more than a brand; it is a family legacy woven into the very fabric of Scottish tradition." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.jamesmackintosh.co.uk/our-story" />
        <link rel="canonical" href="https://www.jamesmackintosh.co.uk/our-story" />
      </Helmet>
      <section className="pt-32 pb-8 md:pt-24 md:pb-20 px-4 md:px-8 bg-hero-bg text-center mb-6 md:mb-16" ref={heroRef}>
          <h1 className="font-serif text-3xl md:text-7xl leading-tight mb-4">Four Generations, One Standard</h1>
          <p className="text-xl text-text opacity-80 max-w-3xl mx-auto">The Mackintosh name is more than a brand; it is a family legacy woven into the very fabric of Scottish tradition.</p>
        </section>
      <div className="pt-0 pb-16 px-4">

        <div className="container mx-auto px-4">
          <section className="py-6 md:py-12 border-b border-border text-center max-w-4xl mx-auto mb-6 md:mb-16 text-base md:text-lg leading-relaxed">
            <p>Our story does not begin with a business plan, but with a single needle and thread in the hands of our great-grandfather. It is a story of dedication passed from one generation to the next, a commitment to the art of kiltmaking that has defined our family for over a century. We do not simply make kilts; we continue a conversation started by those who came before us.</p>
          </section>

          <section className="py-6 md:py-12 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center mb-6 md:mb-16">
              <div className="generation-text order-1 md:order-1">
                <h2 className="font-serif text-2xl md:text-4xl mb-6 text-primary">The First Generation: The Founder's Vision</h2>
                <p className="leading-relaxed mb-4">It began with Alistair Mackintosh, a man who understood that a kilt was not merely a garment, but a symbol of identity. In a small workshop in Glasgow, he established a reputation for uncompromising quality, where the precision of the pleat and the integrity of the stitch were paramount. He laid the foundation not of a company, but of a standard.</p>
              </div>
              <div className="image-placeholder order-2 md:order-2"><img src={mack} alt="Alistair Mackintosh, the founder" className="max-w-[80%] h-auto block mx-auto rounded-lg" /></div>
            </div>
          </section>

          <section className="py-6 md:py-12 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center mb-6 md:mb-16">
              <div className="image-placeholder order-2 md:order-1"><img src={workshop} alt="A vintage photograph of the original workshop" className="max-w-[80%] h-auto block mx-auto rounded-lg" /></div>
              <div className="generation-text order-1 md:order-2">
                <h2 className="font-serif text-2xl md:text-4xl mb-6 text-primary">The Second Generation: The Custodian's Duty</h2>
                <p className="leading-relaxed mb-4">Inheriting his father's craft, Ewan Mackintosh navigated a changing world. He understood that tradition was not about resisting the new, but about preserving what was timeless. It was his careful stewardship that ensured the family's methods survived, even as the world outside the workshop doors was transformed. He was the quiet custodian of a priceless art.</p>
              </div>
            </div>
          </section>

          <section className="py-6 md:py-12 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center mb-6 md:mb-16">
              <div className="generation-text order-1 md:order-1">
                <h2 className="font-serif text-2xl md:text-4xl mb-6 text-primary">The Third Generation: The Innovator's Touch</h2>
                <p className="leading-relaxed mb-4">Liam Mackintosh brought a new perspective, recognizing that heritage could be both honored and advanced. He refined techniques, sourced new materials from old mills, and introduced the Mackintosh name to a wider audience, all without sacrificing the core principles of his grandfather's vision. He proved that innovation and tradition could be powerful allies.</p>
              </div>
              <div className="image-placeholder order-2 md:order-2"><img src={three} alt="A photo of the third-generation owner with a client" className="max-w-[80%] h-auto block mx-auto rounded-lg" /></div>
            </div>
          </section>

          <section className="py-6 md:py-12 border-b border-border">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-12 items-center mb-6 md:mb-16">
              <div className="image-placeholder order-2 md:order-1"><img src={tartan} alt="A contemporary shot of the current workshop" className="max-w-[80%] h-auto block mx-auto rounded-lg" /></div>
              <div className="generation-text order-1 md:order-2">
                <h2 className="font-serif text-2xl md:text-4xl mb-6 text-primary">The Fourth Generation: The Modern Craftsman</h2>
                <p className="leading-relaxed mb-4">Today, we, the fourth generation, carry that legacy forward. We are the beneficiaries of a century of knowledge, and we feel the weight of that responsibility in every kilt we create. We blend the time-honored techniques of our ancestors with a modern understanding of fit and form, ensuring that the Mackintosh kilt is as relevant today as it was in our great-grandfather's time.</p>
              </div>
            </div>
          </section>

          <section className="py-8 md:py-24 px-4 md:px-8 bg-background text-center">
            <h2 className="font-serif text-3xl md:text-5xl mb-6">The Story Continues With You</h2>
            <p className="text-lg leading-relaxed max-w-3xl mx-auto">When you commission a kilt from us, you become part of this story. You are not just a customer, but a patron of a living tradition, and we are honored to share it with you.</p>
          </section>
        </div>
      </div>
    </>
  );
};

export default OurStory;